/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { arrayByLength, clone, equal, uniq } from "../../../../shared";
import { SLIDE_NUMBER } from "./constants";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { getDefaultMark } from './utils/getDefaultMark';

type Parameters = {
  commodities: number[];
  calendar: number[];
  units: number[];
};

export class SlideContext implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;

  public olapResultTable$ = new BehaviorSubject<OLAPResult | null>(null);
  public sources$ = new BehaviorSubject<string | null>(null);
  public loading$ = new BehaviorSubject<boolean>(true);

  protected _parameters: BehaviorSubject<Parameters | undefined> = new BehaviorSubject<Parameters | undefined>(undefined);
  public get parameters(): Observable<Parameters | undefined> {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._initDefaultMark();

    this._parameters
      .pipe(debounceTime(300))
      .subscribe(async (parameters) => {
        if (parameters) {
          this.readyState = ComponentContextReadyState.Loading;
          this.loading$.next(true);
          this.olapResultTable$.next(null);
          this.sources$.next(null);

          const olapResultPromise = this.query(parameters);
          const sourcesPromise = this.fetchSources(parameters);

          const [olapResult, sources] = await Promise.all([ olapResultPromise, sourcesPromise ]);

          this.sources$.next(sources);
          this.olapResultTable$.next(olapResult);
          this.readyState = ComponentContextReadyState.Loaded;
          this.loading$.next(false);
        }
      });
  }

  private async _initDefaultMark() {

    const { dataflowService } = this.options;
    const commoditiesParam = dataflowService.getParameter("@commodities") as MultiValueParameter;
    const calendarParam = dataflowService.getParameter("@calendar") as MultiValueParameter;
    const unitsParam = dataflowService.getParameter("@units") as MultiValueParameter;

    const { serviceProvider } = this.options;
    const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
    const { calendar } = await getDefaultMark(dictionariesService);
    calendarParam.value = calendar;

    this._parameters.next({
      commodities: commoditiesParam.value as number[],
      calendar: calendarParam.value as number[],
      units: unitsParam.value as number[],
    });
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar") as number[];
    const commodities = command.values.get("@commodities") as number[];
    const parameters = clone(this._parameters.value);
    if (parameters !== undefined) {
      if (calendar !== undefined) {
        parameters.calendar = calendar;
      }
      if (commodities !== undefined) {
        parameters.commodities = commodities;
      }

      if (!equal(parameters, this._parameters.value)) {
        this._parameters.next(parameters);
      }
    }
  }

  async fetchSources(parameters: Parameters) {
    const { commodities } = parameters;
    const { dataSourcesService } = this.options;
    const sources = (await Promise.all(
      commodities.map(commodity => dataSourcesService.getDataSources(SLIDE_NUMBER, 1, Number(commodity)))
    )).flatMap(x => x.split(', '));
    return uniq(sources).filter(Boolean).join(', ');
  }

  async query(parameters: Parameters) {
    const { calendar, commodities, units } = parameters;

    const cubeSlideDef = await dataService.getCubeDefinition("facts_fertilizer_prices");
    const cubeSlide = await dataService.getCube({
      id: cubeSlideDef.id,
      parameters: cubeSlideDef.parameters,
    });

    const olapQuery = ({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: calendar,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "commodities.default", selection: commodities, selectionMode: SelectionMode.Include },
          { path: "units.default", selection: units, selectionMode: SelectionMode.Include },
          {
            path: "Measures",
            measures: [
              { id: "value", aggregationType: AggregationType.None },
            ],
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);

    const olapResult = await cubeSlide.query(olapQuery);
    return olapResult;
  }
}
